import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import {
  Home, ChevronRight, Star, MapPin, Users, User, Check, Phone, Lock, UserPlus,
  CheckCircle2, Send, Heart, Share2, Loader2, ExternalLink, MessageCircle, Globe, Facebook, Twitter, Instagram, Youtube,
} from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import VenueCard from "../components/VenueCard";
import MemberDialog from "../components/MemberDialog";
import { getVenue, getCategory, VENUES, formatPrice } from "../mock/mockData";
import { Seo, citySlug, breadcrumbJsonLd, venueJsonLd } from "../lib/seo";
import { useToast } from "../hooks/use-toast";
import { ToastAction } from "../components/ui/toast";
import { useMember } from "../context/MemberContext";
import { useCategories } from "../context/CategoriesContext";
import { isPerPerson } from "../lib/price";
import { Lightbox } from "../components/Lightbox";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VenueDetail = () => {
  const { categories: CATS } = useCategories();
  const { slug } = useParams();
  const { toast } = useToast();
  const { member, token: memberToken, toggleFavorite, isFavorite } = useMember();
  const [favBusy, setFavBusy] = useState(false);
  const [memberDlg, setMemberDlg] = useState(false);
  const fav = isFavorite(slug);

  const handleFav = async () => {
    if (!member) { setMemberDlg(true); return; }
    setFavBusy(true);
    try {
      await toggleFavorite(slug);
      toast({ title: fav ? "Favorilerden çıkarıldı" : "Favorilere eklendi" });
    } catch {
      toast({ title: "Hata", description: "İşlem gerçekleştirilemedi." });
    } finally { setFavBusy(false); }
  };

  const handleShare = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try { await navigator.share({ title: venue?.name || "DüğünSenin", url }); return; } catch {}
    }
    try {
      await navigator.clipboard.writeText(url);
      toast({ title: "Bağlantı kopyalandı", description: "Paylaşmak için yapıştırabilirsiniz." });
    } catch {
      toast({ title: "Bağlantı", description: url });
    }
  };

  const mockVenue = getVenue(slug);
  const [venue, setVenue] = useState(mockVenue || null);
  const [loading, setLoading] = useState(!mockVenue);
  const [activeImg, setActiveImg] = useState(0);
  const [lightbox, setLightbox] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", date: new Date().toLocaleDateString("en-CA"), message: "" });

  useEffect(() => {
    if (member) setForm((p) => ({ ...p, name: p.name || member.name || "", phone: p.phone || member.phone || "" }));
  }, [member]);
  const [sent, setSent] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [reviewForm, setReviewForm] = useState({ name: "", rating: 5, comment: "" });
  const [reviewSent, setReviewSent] = useState(false);
  const [reviewBusy, setReviewBusy] = useState(false);

  const loadReviews = React.useCallback(() => {
    axios.get(`${API}/firms/${slug}/reviews`).then((r) => setReviews(r.data || [])).catch(() => setReviews([]));
  }, [slug]);
  useEffect(() => { loadReviews(); setReviewSent(false); setReviewForm({ name: "", rating: 5, comment: "" }); }, [loadReviews]);

  const submitReview = async () => {
    if (!reviewForm.name.trim() || !reviewForm.comment.trim()) {
      toast({ title: "Eksik bilgi", description: "Lütfen adınızı ve yorumunuzu yazın." });
      return;
    }
    setReviewBusy(true);
    try {
      await axios.post(`${API}/reviews`, { slug, name: reviewForm.name.trim(), rating: reviewForm.rating, comment: reviewForm.comment.trim() });
      setReviewSent(true);
      setReviewForm({ name: "", rating: 5, comment: "" });
      toast({ title: "Yorumunuz alındı", description: "Firma onayından sonra yayınlanacaktır." });
    } catch (e) {
      toast({ title: "Hata", description: e?.response?.data?.detail || "Yorum gönderilemedi." });
    } finally { setReviewBusy(false); }
  };

  const trackClick = (event) => {
    if (venue?.id) {
      axios.post(`${API}/firms/${venue.id}/track/${event}`).catch(() => {});
    }
  };

  const viewedRef = React.useRef(null);
  useEffect(() => {
    if (venue?.id && !venue.previewPending && viewedRef.current !== venue.id) {
      viewedRef.current = venue.id;
      axios.post(`${API}/firms/${venue.id}/track/view`).catch(() => {});
    }
  }, [venue?.id]);

  useEffect(() => {
    window.scrollTo(0, 0);
    setActiveImg(0);
    setSent(false);
    const m = getVenue(slug);
    if (m) {
      setVenue(m);
      setLoading(false);
    } else {
      setLoading(true);
      axios
        .get(`${API}/firms/${slug}`, localStorage.getItem("ds_admin_token") ? { headers: { Authorization: `Bearer ${localStorage.getItem("ds_admin_token")}` } } : undefined)
        .then((res) => setVenue(res.data))
        .catch(() => setVenue(null))
        .finally(() => setLoading(false));
    }
  }, [slug]);

  if (loading) {
    return (
      <div className="bg-[#FBF5F0] min-h-screen">
        <Header />
        <div className="flex items-center justify-center pt-48 pb-40">
          <Loader2 className="w-8 h-8 text-[#9E2A3B] animate-spin" />
        </div>
        <Footer />
      </div>
    );
  }

  if (!venue) {
    return (
      <div className="bg-[#FBF5F0] min-h-screen">
        <Header />
        <div className="max-w-3xl mx-auto px-5 pt-40 pb-20 text-center">
          <h1 className="font-display text-3xl text-[#2A2224]">Mekan bulunamadı</h1>
          <Link to="/" className="inline-block mt-6 text-[#9E2A3B] font-medium link-underline">Anasayfaya dön</Link>
        </div>
        <Footer />
      </div>
    );
  }

  const category = getCategory(venue.category);
  const similar = VENUES.filter((v) => v.category === venue.category && v.slug !== venue.slug).slice(0, 3);

  const submit = async (e) => {
    e.preventDefault();
    if (!member) { setMemberDlg(true); return; }
    if (!form.name || !form.phone) {
      toast({ title: "Eksik bilgi", description: "Lütfen adınızı ve telefonunuzu girin." });
      return;
    }
    try {
      await axios.post(`${API}/quotes`, { slug, name: form.name, phone: form.phone, date: form.date, message: form.message }, { headers: { Authorization: `Bearer ${memberToken}` } });
      setSent(true);
      toast({ title: "Talebiniz alındı!", description: `${venue.name} en kısa sürede sizinle iletişime geçecek.` });
      setForm({ name: "", phone: "", date: new Date().toLocaleDateString("en-CA"), message: "" });
    } catch (err) {
      toast({ title: "Hata", description: err?.response?.data?.detail || "Talebiniz gönderilemedi, lütfen tekrar deneyin." });
    }
  };

  return (
    <div className="bg-[#FBF5F0] min-h-screen">
      {(() => {
        const vCanonical = `/${venue.category}/${citySlug(venue.city || "")}/${venue.slug}`;
        const vTitle = `${venue.name}${venue.city ? ` ${venue.city}` : ""} Fiyatları | DüğünSenin`;
        const vDesc = (venue.description ? venue.description.replace(/\s+/g, " ").slice(0, 155) : `${venue.name} — ${category?.name || ""} ${venue.city || ""} ${venue.district || ""}. Fiyat, kapasite, fotoğraflar ve iletişim bilgileri.`).trim();
        return (
          <Seo
            title={vTitle}
            description={vDesc}
            canonical={vCanonical}
            ogImage={venue.image}
            jsonLd={[
              venueJsonLd(venue, vCanonical),
              breadcrumbJsonLd([
                { name: "Ana Sayfa", url: "/" },
                { name: category?.name || "Kategori", url: `/kategori/${venue.category}` },
                ...(venue.city ? [{ name: venue.city, url: `/${venue.category}/${citySlug(venue.city)}` }] : []),
                { name: venue.name, url: vCanonical },
              ]),
            ]}
          />
        );
      })()}
      <Header />
      {venue.previewPending && (
        <div className="bg-[#FFF3D6] border-b border-[#F1DDB4] text-[#6b5530] text-sm text-center px-4 py-2 fixed top-[72px] inset-x-0 z-40 shadow-sm" data-testid="pending-preview-banner">
          Önizleme: Bu firma <b>onay bekliyor</b> ve ziyaretçilere görünmüyor. Patron Paneli'nden onaylayınca yayına girer.
        </div>
      )}

      <div className="max-w-7xl mx-auto px-5 pt-28">
        <nav className="flex items-center gap-2 text-[#7C6E70] text-sm mb-5 flex-wrap">
          <Link to="/" className="flex items-center gap-1 hover:text-[#9E2A3B]"><Home className="w-3.5 h-3.5" /> Anasayfa</Link>
          <ChevronRight className="w-3.5 h-3.5" />
          <Link to={`/kategori/${venue.category}`} className="hover:text-[#9E2A3B]">{category?.name}</Link>
          <ChevronRight className="w-3.5 h-3.5" />
          <span className="text-[#9E2A3B]">{venue.name}</span>
        </nav>
      </div>

      {/* Gallery */}
      {lightbox && <Lightbox images={venue.gallery} captions={venue.captions || {}} start={activeImg} name={venue.name} onClose={() => setLightbox(false)} />}
      <div className="max-w-7xl mx-auto px-5">
        <div className="grid grid-cols-1 lg:grid-cols-[1.6fr_1fr] gap-4">
          <div className="rounded-2xl overflow-hidden h-[280px] md:h-[460px] relative">
            <img src={venue.gallery[activeImg]} alt={venue.captions?.[venue.gallery[activeImg]] || venue.name} onClick={() => setLightbox(true)} data-testid="venue-main-image" className="w-full h-full object-cover animate-fade-in cursor-zoom-in" key={activeImg} />
            {venue.captions?.[venue.gallery[activeImg]] && (
              <span className="absolute left-4 bottom-4 max-w-[55%] truncate px-3.5 py-1.5 rounded-full bg-black/55 backdrop-blur-md text-white text-sm" data-testid="venue-main-caption">
                {venue.captions[venue.gallery[activeImg]]}
              </span>
            )}
            {venue.gallery.length > 1 && (
              <button onClick={() => setLightbox(true)} data-testid="venue-open-gallery" className="absolute right-4 bottom-4 px-3.5 py-1.5 rounded-full bg-white/90 text-[#9E2A3B] text-sm font-medium shadow hover:bg-white transition-colors">
                Tüm Fotoğraflar ({venue.gallery.length})
              </button>
            )}
            <div className="absolute top-4 right-4 flex gap-2">
              <button
                onClick={handleFav}
                disabled={favBusy}
                data-testid="venue-favorite-btn"
                aria-label="Favorilere ekle"
                className={`w-10 h-10 rounded-full flex items-center justify-center transition-all shadow disabled:opacity-60 ${fav ? "bg-[#9E2A3B] text-white" : "bg-white/90 text-[#9E2A3B] hover:bg-white"}`}
              >
                <Heart className="w-5 h-5" fill={fav ? "currentColor" : "none"} />
              </button>
              <button
                onClick={handleShare}
                data-testid="venue-share-btn"
                aria-label="Paylaş"
                className="w-10 h-10 rounded-full bg-white/90 flex items-center justify-center text-[#9E2A3B] hover:bg-white transition-colors shadow"
              >
                <Share2 className="w-5 h-5" />
              </button>
            </div>
          </div>
          <div className="grid grid-cols-3 lg:grid-cols-2 gap-4 lg:h-[460px] lg:overflow-y-auto lg:content-start lg:pr-1">
            {venue.gallery.map((g, i) => (
              <button
                key={i}
                onClick={() => { setActiveImg(i); if (i === activeImg) setLightbox(true); }}
                onDoubleClick={() => { setActiveImg(i); setLightbox(true); }}
                data-testid={`venue-thumb-${i}`}
                className={`relative img-zoom rounded-2xl overflow-hidden h-[120px] md:h-[222px] ${
                  i === activeImg ? "ring-2 ring-[#9E2A3B] ring-offset-2" : "opacity-85 hover:opacity-100"
                }`}
              >
                <img src={g} alt={venue.captions?.[g] || `${venue.name} fotoğraf ${i + 1}`} className="w-full h-full object-cover" />
                {venue.captions?.[g] && (
                  <span className="absolute inset-x-0 bottom-0 px-2.5 py-1.5 bg-gradient-to-t from-black/70 to-transparent text-white text-[11px] md:text-xs text-left truncate" data-testid={`venue-thumb-caption-${i}`}>{venue.captions[g]}</span>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-5 py-10 grid grid-cols-1 lg:grid-cols-[1fr_400px] gap-8">
        {/* Left */}
        <div>
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <h1 className="font-display text-3xl md:text-4xl font-bold text-[#2A2224]">{venue.name}</h1>
              <div className="flex items-center gap-4 mt-3 text-[#7C6E70]">
                <span className="flex items-center gap-1"><MapPin className="w-4 h-4 text-[#C15B6B]" /> {venue.district}, {venue.city}</span>
                {venue.reviews > 0 && <span className="flex items-center gap-1" data-testid="venue-header-rating"><Star className="w-4 h-4 text-[#C6A15B]" fill="#C6A15B" /> {venue.rating} ({venue.reviews} değerlendirme)</span>}
              </div>
            </div>
            {venue.featured && (
              <span className="px-4 py-1.5 rounded-full bg-[#C6A15B] text-white text-sm font-medium">Öne Çıkan</span>
            )}
          </div>

          {(venue.website || venue.facebook || venue.twitter || venue.instagram || venue.youtube) && (
            <div className="mt-5 flex flex-wrap items-center gap-3" data-testid="social-links">
              <span className="text-sm font-medium text-[#7C6E70] mr-1">Bizi Takip Edin:</span>
              {venue.website && (
                <a href={venue.website} onClick={() => trackClick("website")} target="_blank" rel="noopener noreferrer" title="Website" data-testid="social-website" className="w-11 h-11 rounded-full bg-[#5b4d4f] flex items-center justify-center text-white shadow-md hover:-translate-y-1 hover:shadow-lg transition-all">
                  <Globe className="w-5 h-5" />
                </a>
              )}
              {venue.instagram && (
                <a href={venue.instagram} onClick={() => trackClick("instagram")} target="_blank" rel="noopener noreferrer" title="Instagram" data-testid="social-instagram" className="w-11 h-11 rounded-full flex items-center justify-center text-white shadow-md hover:-translate-y-1 hover:shadow-lg transition-all" style={{ background: "linear-gradient(45deg,#F58529,#DD2A7B,#8134AF)" }}>
                  <Instagram className="w-5 h-5" />
                </a>
              )}
              {venue.facebook && (
                <a href={venue.facebook} onClick={() => trackClick("facebook")} target="_blank" rel="noopener noreferrer" title="Facebook" data-testid="social-facebook" className="w-11 h-11 rounded-full bg-[#1877F2] flex items-center justify-center text-white shadow-md hover:-translate-y-1 hover:shadow-lg transition-all">
                  <Facebook className="w-5 h-5" />
                </a>
              )}
              {venue.twitter && (
                <a href={venue.twitter} onClick={() => trackClick("twitter")} target="_blank" rel="noopener noreferrer" title="Twitter" data-testid="social-twitter" className="w-11 h-11 rounded-full bg-[#1DA1F2] flex items-center justify-center text-white shadow-md hover:-translate-y-1 hover:shadow-lg transition-all">
                  <Twitter className="w-5 h-5" />
                </a>
              )}
              {venue.youtube && (
                <a href={venue.youtube} onClick={() => trackClick("youtube")} target="_blank" rel="noopener noreferrer" title="YouTube" data-testid="social-youtube" className="w-11 h-11 rounded-full bg-[#FF0000] flex items-center justify-center text-white shadow-md hover:-translate-y-1 hover:shadow-lg transition-all">
                  <Youtube className="w-5 h-5" />
                </a>
              )}
            </div>
          )}

          <div className="flex flex-wrap items-stretch gap-3 mt-5" data-testid="rating-boxes">
            <div
              data-testid="dugunsenin-rating-box"
              className="inline-flex items-center gap-2.5 px-5 py-3 rounded-xl bg-white border border-[#F0DDE0] shadow-sm"
            >
              <span className="flex items-center justify-center w-8 h-8 rounded-full bg-[#FBEEF0]">
                <Heart className="w-4 h-4 text-[#9E2A3B]" fill="#9E2A3B" />
              </span>
              <span className="flex flex-col leading-tight">
                <span className="text-sm font-semibold text-[#2A2224]">DüğünSenin Puanı</span>
                <span className="text-xs text-[#7C6E70] flex items-center gap-1">
                  {reviews.length ? (
                    <>
                      <span className="flex items-center gap-0.5">
                        {[1, 2, 3, 4, 5].map((n) => {
                          const avg = reviews.reduce((s, r) => s + (r.rating || 0), 0) / reviews.length;
                          return <Star key={n} className={`w-3.5 h-3.5 ${n <= Math.round(avg) ? "text-[#C6A15B]" : "text-[#E3D6D8]"}`} fill={n <= Math.round(avg) ? "#C6A15B" : "#E3D6D8"} />;
                        })}
                      </span>
                      <span className="ml-1 font-medium text-[#2A2224]">{(reviews.reduce((s, r) => s + (r.rating || 0), 0) / reviews.length).toFixed(1)}</span>
                      <span>({reviews.length} yorum)</span>
                    </>
                  ) : (
                    <span>Henüz yorum yok</span>
                  )}
                </span>
              </span>
            </div>

            {venue.googleUrl && (
              <a
                data-testid="google-reviews-button"
                href={venue.googleUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2.5 px-5 py-3 rounded-xl bg-white border border-[#F0DDE0] shadow-sm hover:shadow-md hover:border-[#E7A9B0] transition-all group"
              >
                <span className="flex items-center justify-center w-8 h-8 rounded-full bg-white border border-[#EEE]">
                  <svg className="w-4 h-4" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
                  </svg>
                </span>
                <span className="flex flex-col leading-tight">
                  <span className="text-sm font-semibold text-[#2A2224]">Google Yorumları</span>
                  <span className="text-xs text-[#7C6E70]">Google'daki değerlendirmeleri görüntüle</span>
                </span>
                <ExternalLink className="w-4 h-4 text-[#9E2A3B] ml-1 group-hover:translate-x-0.5 transition-transform" />
              </a>
            )}
          </div>

          <div className="flex flex-wrap gap-4 mt-6">
            {(CATS.find((c) => c.slug === venue.category) || {}).group === "mekan" && (
            <div className="flex-1 min-w-[150px] bg-white rounded-xl border border-[#F0DDE0] p-4" data-testid="venue-capacity">
              <span className="flex items-center gap-2 text-[#7C6E70] text-sm"><Users className="w-4 h-4 text-[#C15B6B]" /> Kapasite</span>
              <p className="font-display text-lg font-semibold text-[#2A2224] mt-1">{venue.capacity}</p>
            </div>
            )}
            <div className="flex-1 min-w-[150px] bg-white rounded-xl border border-[#F0DDE0] p-4">
              <span className="text-[#7C6E70] text-sm">Başlangıç Fiyatı</span>
              <p className="font-display text-lg font-semibold text-[#9E2A3B] mt-1">
                {venue.priceFrom > 0 ? formatPrice(venue.priceFrom) : "Teklif alın"}
                {isPerPerson(venue.priceFrom, (CATS.find((c) => c.slug === venue.category) || {}).group, venue.priceType) && <span className="text-sm font-sans font-medium text-[#7C6E70]" data-testid="detail-price-per-person"> / kişi başı</span>}
              </p>
            </div>
          </div>

          <div className="mt-8">
            <h2 className="font-display text-2xl font-semibold text-[#2A2224] mb-3">Hakkında</h2>
            <p className="text-[#5b4d4f] leading-relaxed whitespace-pre-line">{venue.description}</p>
          </div>

          {venue.features?.length > 0 && (
          <div className="mt-8">
            <h2 className="font-display text-2xl font-semibold text-[#2A2224] mb-4">Özellikler & Hizmetler</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {venue.features.map((f) => (
                <div key={f} className="flex items-center gap-2 bg-white rounded-xl border border-[#F0DDE0] px-4 py-3">
                  <Check className="w-4 h-4 text-[#9E2A3B]" />
                  <span className="text-sm text-[#2A2224]">{f}</span>
                </div>
              ))}
            </div>
          </div>
          )}
        </div>

        {/* Right: Quote form */}
        <aside className="lg:sticky lg:top-24 h-fit">
          <div className="bg-white rounded-2xl border border-[#F0DDE0] shadow-[0_20px_50px_-25px_rgba(158,42,59,0.35)] overflow-hidden">
            <div className="bg-gradient-to-r from-[#C15B6B] to-[#9E2A3B] px-6 py-5">
              <h3 className="font-display text-xl font-bold text-white">Ücretsiz Fiyat Teklifi Al</h3>
              <p className="text-white/85 text-sm mt-1">Dakikalar içinde geri dönüş yapılır</p>
              {venue.contactName && (
                <p className="text-white/95 text-sm mt-2 flex items-center gap-1.5" data-testid="quote-firm-contact">
                  <User className="w-4 h-4" /> Firma Yetkilisi: <span className="font-semibold">{venue.contactName}</span>
                </p>
              )}
            </div>

            {sent ? (
              <div className="p-8 text-center">
                <CheckCircle2 className="w-14 h-14 text-[#9E2A3B] mx-auto mb-4" />
                <h4 className="font-display text-xl font-semibold text-[#2A2224]">Teşekkürler!</h4>
                <p className="text-[#7C6E70] mt-2 text-sm">Talebiniz alındı. En kısa sürede sizinle iletişime geçeceğiz.</p>
                <button onClick={() => setSent(false)} className="mt-5 text-[#9E2A3B] font-medium link-underline">Yeni talep oluştur</button>
              </div>
            ) : (
              <div className="p-6 space-y-4">
                {member ? (
                <form onSubmit={submit} className="space-y-4" data-testid="quote-form">
                <div>
                  <label className="text-sm text-[#5b4d4f] mb-1.5 block">Ad Soyad</label>
                  <input
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="w-full h-11 px-4 rounded-xl bg-[#FBF5F0] border border-transparent focus:border-[#E7A9B0] outline-none text-[#2A2224]"
                    placeholder="Adınız Soyadınız"
                  />
                </div>
                <div>
                  <label className="text-sm text-[#5b4d4f] mb-1.5 block">Telefon</label>
                  <input
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    className="w-full h-11 px-4 rounded-xl bg-[#FBF5F0] border border-transparent focus:border-[#E7A9B0] outline-none text-[#2A2224]"
                    placeholder="05XX XXX XX XX"
                  />
                </div>
                <div>
                  <label className="text-sm text-[#5b4d4f] mb-1.5 block">Düğün Tarihi</label>
                  <input
                    type="date"
                    value={form.date}
                    onChange={(e) => setForm({ ...form, date: e.target.value })}
                    className="w-full h-11 px-4 rounded-xl bg-[#FBF5F0] border border-transparent focus:border-[#E7A9B0] outline-none text-[#2A2224]"
                  />
                </div>
                <div>
                  <label className="text-sm text-[#5b4d4f] mb-1.5 block">Mesajınız</label>
                  <textarea
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    rows={3}
                    className="w-full px-4 py-3 rounded-xl bg-[#FBF5F0] border border-transparent focus:border-[#E7A9B0] outline-none text-[#2A2224] resize-none"
                    placeholder="Firmadan isteklerinizi yazın..."
                  />
                </div>
                <button type="submit" data-testid="quote-submit-button" className="w-full flex items-center justify-center gap-2 h-12 rounded-xl bg-gradient-to-r from-[#C15B6B] to-[#9E2A3B] text-white font-medium shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all">
                  <Send className="w-4 h-4" /> Teklif İste
                </button>
                </form>
                ) : (
                <div className="rounded-xl bg-[#FBF0F2] border border-[#F0DDE0] p-5 text-center" data-testid="quote-login-required">
                  <Lock className="w-8 h-8 mx-auto text-[#9E2A3B]" />
                  <p className="font-display text-lg text-[#2A2224] mt-2">Teklif istemek için üye olun</p>
                  <p className="text-sm text-[#7C6E70] mt-1">Ücretsiz üye olun; tekliflerinizi, favorilerinizi ve düğün araçlarınızı tek panelden yönetin.</p>
                  <button onClick={() => setMemberDlg(true)} data-testid="quote-login-button" className="mt-4 w-full flex items-center justify-center gap-2 h-11 rounded-xl bg-gradient-to-r from-[#C15B6B] to-[#9E2A3B] text-white font-medium shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all">
                    <UserPlus className="w-4 h-4" /> Üye Ol / Giriş Yap
                  </button>
                  <p className="text-xs text-[#7C6E70] mt-3">Telefon ve yol tarifi üyelere özeldir. Üye olmadan WhatsApp ile yazabilirsiniz 👇</p>
                </div>
                )}
                {member && venue.publicPhone && (
                  <a onClick={() => trackClick("phone")} href={`tel:${venue.publicPhone.replace(/[^0-9+]/g, "")}`} data-testid="call-button" className="w-full flex items-center justify-center gap-2 h-12 rounded-xl border border-[#9E2A3B] text-[#9E2A3B] font-medium hover:bg-[#F6E4E6] transition-colors">
                    <Phone className="w-4 h-4" /> Hemen Ara
                  </a>
                )}
                {venue.whatsappUrl && (
                  <a
                    data-testid="whatsapp-button"
                    onClick={() => {
                      trackClick("whatsapp");
                      if (!member) toast({
                        title: "Ücretsiz üye olun",
                        description: "Firmanın telefonunu ve konumunu görmek için hemen üye olun.",
                        duration: 8000,
                        action: <ToastAction altText="Üye Ol" onClick={() => setMemberDlg(true)} data-testid="wa-member-invite-btn">Üye Ol</ToastAction>,
                      });
                    }}
                    href={`${venue.whatsappUrl}${venue.whatsappUrl.includes("?") ? "&" : "?"}text=${encodeURIComponent("Merhaba, size dugunsenin.com'dan ulaşıyorum. Hizmetleriniz hakkında bilgi almak istiyorum.")}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full flex items-center justify-center gap-2 h-12 rounded-xl bg-[#25D366] text-white font-medium shadow-md hover:bg-[#1EBE5A] hover:shadow-lg transition-all"
                  >
                    <MessageCircle className="w-4 h-4" /> WhatsApp'tan Yaz
                  </a>
                )}
                {member && venue.mapUrl && (
                  <a
                    data-testid="directions-button"
                    onClick={() => trackClick("maps")}
                    href={/^https?:\/\//.test(venue.mapUrl) ? venue.mapUrl : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(venue.mapUrl)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full flex items-center justify-center gap-2 h-12 rounded-xl border border-[#9E2A3B] text-[#9E2A3B] font-medium hover:bg-[#F6E4E6] transition-colors"
                  >
                    <MapPin className="w-4 h-4" /> Yol Tarifi Al
                  </a>
                )}
              </div>
            )}
          </div>
        </aside>
      </div>

      {member && venue.mapUrl && !/^https?:\/\//.test(venue.mapUrl) && (
        <div className="max-w-7xl mx-auto px-5 pb-12" data-testid="venue-map">
          <h2 className="font-display text-2xl font-bold text-[#2A2224] mb-4">Konum</h2>
          <div className="rounded-2xl overflow-hidden border border-[#F0DDE0] max-w-md">
            <iframe
              title="Harita"
              width="100%"
              height="220"
              style={{ border: 0 }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              src={`https://maps.google.com/maps?q=${encodeURIComponent(venue.mapUrl)}&output=embed`}
            />
          </div>
        </div>
      )}

      {/* YORUMLAR */}
      <div className="max-w-7xl mx-auto px-5 pb-12" data-testid="venue-reviews">
        <h2 className="font-display text-2xl md:text-3xl font-bold text-[#2A2224] mb-6 flex items-center gap-2">
          <MessageCircle className="w-6 h-6 text-[#C15B6B]" /> Yorumlar
          {reviews.length > 0 && <span className="text-base font-normal text-[#7C6E70]">({reviews.length})</span>}
        </h2>
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_400px] gap-8">
          {/* Liste */}
          <div className="space-y-4">
            {reviews.length === 0 ? (
              <div className="bg-white rounded-2xl border border-[#F0DDE0] p-8 text-center text-[#7C6E70]" data-testid="reviews-empty">
                Henüz yayınlanmış yorum yok. İlk yorumu siz yazın!
              </div>
            ) : (
              reviews.map((r) => (
                <div key={r.id} className="bg-white rounded-2xl border border-[#F0DDE0] p-5" data-testid="review-item">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-[#F6E4E6] flex items-center justify-center text-[#9E2A3B] font-semibold">
                        {(r.name || "?").charAt(0).toUpperCase()}
                      </div>
                      <span className="font-medium text-[#2A2224]">{r.name}</span>
                    </div>
                    <div className="flex items-center gap-0.5">
                      {[1, 2, 3, 4, 5].map((n) => (
                        <Star key={n} className={`w-4 h-4 ${n <= r.rating ? "text-[#C6A15B]" : "text-[#E3D6D8]"}`} fill={n <= r.rating ? "#C6A15B" : "#E3D6D8"} />
                      ))}
                    </div>
                  </div>
                  <p className="text-[#5b4d4f] text-sm mt-3 leading-relaxed">{r.comment}</p>
                </div>
              ))
            )}
          </div>

          {/* Form */}
          <div className="bg-white rounded-2xl border border-[#F0DDE0] p-6 h-max lg:sticky lg:top-24" data-testid="review-form">
            <h3 className="font-display text-xl text-[#2A2224]">Yorum Yaz</h3>
            <p className="text-sm text-[#7C6E70] mt-1 mb-4">Yorumunuz firma onayından sonra yayınlanır.</p>
            {reviewSent ? (
              <div className="text-center py-8" data-testid="review-sent">
                <CheckCircle2 className="w-12 h-12 mx-auto text-[#2E8B57]" />
                <p className="font-display text-lg text-[#2A2224] mt-3">Teşekkürler!</p>
                <p className="text-sm text-[#7C6E70] mt-1">Yorumunuz firma onayına gönderildi.</p>
                <button onClick={() => setReviewSent(false)} className="mt-4 text-sm text-[#9E2A3B] hover:underline">Yeni yorum yaz</button>
              </div>
            ) : (
              <div className="space-y-3">
                <input
                  value={reviewForm.name}
                  onChange={(e) => setReviewForm((p) => ({ ...p, name: e.target.value }))}
                  placeholder="Adınız"
                  data-testid="review-name-input"
                  className="w-full h-11 px-4 rounded-xl bg-[#FBF5F0] outline-none text-[#2A2224] placeholder:text-[#a8999b] focus:ring-2 focus:ring-[#C15B6B]"
                />
                <div className="flex items-center gap-1.5">
                  <span className="text-sm text-[#7C6E70] mr-1">Puanınız:</span>
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button key={n} type="button" onClick={() => setReviewForm((p) => ({ ...p, rating: n }))} data-testid={`review-star-${n}`}>
                      <Star className={`w-6 h-6 transition-colors ${n <= reviewForm.rating ? "text-[#C6A15B]" : "text-[#E3D6D8]"}`} fill={n <= reviewForm.rating ? "#C6A15B" : "#E3D6D8"} />
                    </button>
                  ))}
                </div>
                <textarea
                  value={reviewForm.comment}
                  onChange={(e) => setReviewForm((p) => ({ ...p, comment: e.target.value }))}
                  placeholder="Deneyiminizi paylaşın..."
                  rows={4}
                  data-testid="review-comment-input"
                  className="w-full px-4 py-3 rounded-xl bg-[#FBF5F0] outline-none text-[#2A2224] placeholder:text-[#a8999b] resize-none focus:ring-2 focus:ring-[#C15B6B]"
                />
                <button
                  onClick={submitReview}
                  disabled={reviewBusy}
                  data-testid="review-submit-button"
                  className="w-full h-11 rounded-xl bg-gradient-to-r from-[#C15B6B] to-[#9E2A3B] text-white font-medium flex items-center justify-center gap-2 hover:shadow-md transition-all disabled:opacity-70"
                >
                  {reviewBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Gönder
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Similar */}
      {similar.length > 0 && (
        <div className="max-w-7xl mx-auto px-5 pb-16">
          <h2 className="font-display text-2xl md:text-3xl font-bold text-[#2A2224] mb-6">Benzer Mekanlar</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {similar.map((v, i) => (
              <VenueCard key={v.id} venue={v} index={i} />
            ))}
          </div>
        </div>
      )}

      <Footer />
      <MemberDialog open={memberDlg} onOpenChange={setMemberDlg} />
    </div>
  );
};

export default VenueDetail;
